import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, MapPin, Clock, Ticket } from 'lucide-react';
import Hero from '../components/Hero';
import SectionHeader from '../components/SectionHeader';
import Reveal from '../components/Reveal';
import { aboutInfo, animals, safariZones, timings, facilities, galleryImages, nearbyPlaces } from '../mock';
import * as Icons from 'lucide-react';

export default function Home() {
  return (
    <div>
      {/* Ticker */}
      <div className="ticker">
        <div className="ticker-track">
          ★  Welcome to India’s largest Lion Safari  •  Open Tuesday to Sunday  •  Guided Naturalist Tours available  •  Explore 350 hectares of pristine wilderness  ★
        </div>
      </div>

      <Hero />

      {/* Quick Info Cards */}
      <section className="section pt-16">
        <div className="container-x grid md:grid-cols-3 gap-6">
          {[
            { icon: Clock, title: 'Timings', text: 'Tues – Sun\n9:00 AM – 4:00 PM' },
            { icon: Ticket, title: 'Ticket', text: 'Starts from ₹65\nBook at counter or online' },
            { icon: MapPin, title: 'Location', text: 'Etawah–Gwalior Road\nEtawah, U.P. 206001' },
          ].map((c, i) => (
            <Reveal key={i} delay={i * 120}>
              <div className="bg-white rounded-xl p-7 flex gap-4 border border-[#e6d3b0] hover:shadow-lg hover:-translate-y-1 transition-all">
                <div className="w-14 h-14 rounded-full bg-[#fbf3e4] flex items-center justify-center text-[#b8791c] flex-shrink-0">
                  <c.icon size={26} />
                </div>
                <div>
                  <h3 className="font-display text-xl text-[#2b1d0e] mb-1">{c.title}</h3>
                  <p className="text-sm text-[#5c4020] whitespace-pre-line leading-relaxed">{c.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* About */}
      <section className="section pt-0">
        <div className="container-x grid md:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1548417153-e49c8d900980?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="lion"
                className="rounded-lg shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#2b1d0e] text-[#f7e6c2] p-6 rounded-lg shadow-xl hidden md:block">
                <div className="font-display text-4xl text-[#f0b040]">2014</div>
                <div className="text-xs uppercase tracking-widest">Established</div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div>
              <SectionHeader eyebrow="About the Safari" title={aboutInfo.title} subtitle={aboutInfo.body} />
              <div className="grid grid-cols-2 gap-4 mb-6">
                {aboutInfo.stats.map((s) => (
                  <div key={s.label} className="border-l-2 border-[#b8791c] pl-4">
                    <div className="font-display text-3xl text-[#2b1d0e] font-bold">{s.value}</div>
                    <div className="text-xs uppercase tracking-widest text-[#8b6520]">{s.label}</div>
                  </div>
                ))}
              </div>
              <Link to="/about" className="btn-primary">
                Read More <ChevronRight size={18} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Safari Zones */}
      <section className="section bg-[#fbf3e4]">
        <div className="container-x">
          <Reveal>
            <SectionHeader
              eyebrow="Explore"
              title="Safari Zones"
              subtitle="Four dedicated safari zones, each home to remarkable wildlife thriving in a semi-wild sanctuary."
              center
            />
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {safariZones.map((z, i) => (
              <Reveal key={z.title} delay={i * 100}>
                <div className="card-tilt bg-white rounded-lg overflow-hidden shadow-sm">
                  <div className="h-56 overflow-hidden">
                    <img src={z.img} alt={z.title} className="img-zoom w-full h-full object-cover" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-xl text-[#2b1d0e] mb-2">{z.title}</h3>
                    <p className="text-sm text-[#5c4020] leading-relaxed">{z.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Animals preview */}
      <section className="section">
        <div className="container-x">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
              <div>
                <span className="eyebrow">Wildlife</span>
                <h2 className="font-display text-4xl md:text-5xl font-bold text-[#2b1d0e] mt-2">Meet Our Residents</h2>
                <div className="h-divider mt-4" />
              </div>
              <Link to="/animals" className="text-[#b8791c] font-semibold flex items-center gap-1 hover:text-[#9a6314]">
                See all animals <ChevronRight size={16} />
              </Link>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {animals.slice(0, 3).map((a, i) => (
              <Reveal key={a.name} delay={i * 100}>
                <div className="card-tilt rounded-lg overflow-hidden bg-white shadow-md">
                  <div className="h-72 overflow-hidden">
                    <img src={a.img} alt={a.name} className="img-zoom w-full h-full object-cover" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-2xl text-[#2b1d0e]">{a.name}</h3>
                    <p className="text-xs italic text-[#8b6520] mb-3">{a.scientific}</p>
                    <p className="text-sm text-[#5c4020] leading-relaxed">{a.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section className="section bg-[#2b1d0e] text-[#f7e6c2] relative overflow-hidden grain">
        <div className="container-x relative z-10">
          <Reveal>
            <div className="text-center mb-14">
              <span className="eyebrow text-[#f0b040]">Visitor Facilities</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-[#fff8ec] mt-2">A Complete Experience</h2>
              <div className="h-divider mx-auto mt-4" />
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {facilities.map((f, i) => {
              const Icon = Icons[f.icon] || Icons.Star;
              return (
                <Reveal key={f.title} delay={i * 90}>
                  <div className="p-6 border border-[#4a3418] rounded-lg hover:border-[#b8791c] hover:bg-[#3d2a12] transition-all">
                    <div className="w-12 h-12 rounded-full bg-[#b8791c]/20 text-[#f0b040] flex items-center justify-center mb-4">
                      <Icon size={22} />
                    </div>
                    <h3 className="font-display text-xl mb-2 text-[#fff8ec]">{f.title}</h3>
                    <p className="text-sm text-[#c9a35c] leading-relaxed">{f.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Nearby Places */}
      <section className="section">
        <div className="container-x">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
              <div>
                <span className="eyebrow">Around Etawah</span>
                <h2 className="font-display text-4xl md:text-5xl font-bold text-[#2b1d0e] mt-2">Tourist Places Nearby</h2>
                <div className="h-divider mt-4" />
                <p className="text-[#5c4020] mt-4 max-w-2xl leading-relaxed">
                  Extend your safari trip with wetlands, ancient temples and river forts – Etawah has something for every traveller.
                </p>
              </div>
              <Link to="/nearby" className="text-[#b8791c] font-semibold flex items-center gap-1 hover:text-[#9a6314]">
                Explore all places <ChevronRight size={16} />
              </Link>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {nearbyPlaces.slice(0, 3).map((p, i) => (
              <Reveal key={p.name} delay={i * 100}>
                <div className="card-tilt bg-white rounded-lg overflow-hidden shadow-md border border-[#e6d3b0] h-full flex flex-col">
                  <div className="h-56 overflow-hidden">
                    <img src={p.img} alt={p.name} className="img-zoom w-full h-full object-cover" />
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <span className="text-[10px] uppercase tracking-widest text-[#b8791c] font-semibold mb-1">{p.category}</span>
                    <h3 className="font-display text-xl text-[#2b1d0e]">{p.name}</h3>
                    <div className="flex items-center gap-1.5 text-xs text-[#8b6520] mt-1 mb-3">
                      <MapPin size={12} />
                      <span>{p.distance}</span>
                    </div>
                    <p className="text-sm text-[#5c4020] leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timings snapshot */}
      <section className="section">
        <div className="container-x grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <SectionHeader eyebrow="Plan Ahead" title="Visiting Hours" subtitle="Etawah Lion Safari welcomes guests through the year, subject to seasonal timings and weekly closure." />
            <div className="space-y-3">
              {timings.map((t) => (
                <div key={t.period} className="flex justify-between border-b border-[#e6d3b0] pb-3">
                  <span className="text-[#3d2a12] font-medium">{t.period}</span>
                  <span className="text-[#b8791c] font-semibold">{t.hours}</span>
                </div>
              ))}
            </div>
            <Link to="/tickets" className="btn-primary mt-8">View Ticket Details <ChevronRight size={18} /></Link>
          </Reveal>
          <Reveal delay={200}>
            <div className="grid grid-cols-2 gap-4">
              {galleryImages.slice(0, 4).map((img, i) => (
                <div key={i} className="gallery-item aspect-[4/5]">
                  <img src={img} alt="safari" />
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1751931817996-368c9ee352ee?crop=entropy&cs=srgb&fm=jpg&q=85)` }}
        />
        <div className="absolute inset-0 bg-[#1a1108]/75" />
        <div className="container-x relative z-10 text-center">
          <Reveal>
            <span className="eyebrow text-[#f0b040]">Adventure Awaits</span>
            <h2 className="font-display text-4xl md:text-6xl text-[#fff8ec] font-bold mt-3 leading-tight">
              Ready for the Wild?
            </h2>
            <p className="text-[#f7e6c2] max-w-xl mx-auto mt-4 mb-8">
              Book your safari today and witness nature’s magnificence up close.
            </p>
            <Link to="/tickets" className="btn-primary text-base">
              Book Your Safari <ChevronRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
